// src/utils/firebase/activity.js
import { 
  collection, 
  addDoc, 
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase.utils';

/**
 * Recursively sanitizes an object to remove null/undefined cardPrintNumber and cardSupply fields
 * from collectedCards arrays. This prevents Firestore errors when logging activity.
 * @param {*} obj - Object to sanitize
 * @returns {*} Sanitized object
 */
const sanitizeActivityDetails = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // If it's an array, process each element
  if (Array.isArray(obj)) {
    // Check if this is a collectedCards array
    if (obj.length > 0 && obj[0] && typeof obj[0] === 'object' && 'cardParallelName' in obj[0]) {
      // This is a collectedCards array - sanitize each card
      return obj.map((card) => {
        const sanitized = {
          cardParallelName: card.cardParallelName,
          cardColor: card.cardColor,
          cardQty: card.cardQty
        };
        // Only include cardPrintNumber if it has a value (not null, not undefined)
        if (card.cardPrintNumber != null) {
          sanitized.cardPrintNumber = card.cardPrintNumber;
        }
        // Only include cardSupply if it has a value (not null, not undefined)
        if (card.cardSupply != null) {
          sanitized.cardSupply = card.cardSupply;
        }
        // Only include cardOdds if it has a value (not null, not undefined)
        if (card.cardOdds != null) {
          sanitized.cardOdds = card.cardOdds;
        }
        return sanitized;
      });
    } else {
      // Regular array - recursively process each element
      return obj.map(item => sanitizeActivityDetails(item));
    }
  }

  // If it's an object, recursively process its properties
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        sanitized[key] = sanitizeActivityDetails(obj[key]);
      }
    }
    return sanitized;
  }

  // Primitive value - return as is
  return obj;
};

/**
 * Logs a user action to the activity log
 * @param {string} userID - User ID
 * @param {string} actionType - Type of action
 * @param {Object} details - Action details
 * @returns {Promise<void>}
 */
export const logUserAction = async (userID, actionType, details = {}) => {
  try {
    // Sanitize details to remove null/undefined cardPrintNumber and cardSupply
    const sanitizedDetails = sanitizeActivityDetails(details);
    
    const logRef = collection(db, `users/${userID}/activityLog`);
    const logEntry = {
      actionType,
      timestamp: Timestamp.now(),
      details: sanitizedDetails,
    };
    await addDoc(logRef, logEntry);
  } catch (error) {
    // Error logging user action - fail silently
  }
};

/**
 * Stores a pending email change when verification email is sent.
 * Activity will be logged only when the user confirms (clicks the link).
 * Replaces any existing pending doc (handles multiple requests before confirmation).
 */
export const storePendingEmailChange = async (uid, oldEmail, newEmail) => {
  try {
    const pendingRef = doc(db, 'users', uid, 'pendingEmailChange', 'email');
    await deleteDoc(pendingRef).catch(() => {}); // Clear any existing pending first
    await setDoc(pendingRef, {
      oldEmail,
      newEmail,
      requestedAt: Timestamp.now(),
    });
  } catch (error) {
    // Error storing pending email change
  }
};

/**
 * If the user just confirmed an email change (currentEmail matches a pending newEmail),
 * logs the activity and clears the pending doc. Call this in onAuthStateChanged.
 * Also cleans up orphaned pending docs (user never verified, or verified with old link).
 */
export const checkAndLogConfirmedEmailChange = async (uid, currentEmail) => {
  try {
    const pendingRef = doc(db, 'users', uid, 'pendingEmailChange', 'email');
    const pendingSnap = await getDoc(pendingRef);
    if (!pendingSnap.exists()) return;

    const { oldEmail, newEmail } = pendingSnap.data();

    if (newEmail === currentEmail) {
      // User confirmed - log the activity
      await logUserAction(uid, 'updatedAccount', {
        accountActionType: 'email',
        oldEmail,
        newEmail,
      });
    }
    // Always delete: either we logged (confirmed), or it's stale (never verified / old link)
    await deleteDoc(pendingRef);
  } catch (error) {
    // Error checking/logging confirmed email change
  }
};

/**
 * Logs a token action to the activity log
 * @param {string} userID - User ID
 * @param {string} actionType - Type of action
 * @param {Object} details - Action details
 * @returns {Promise<void>}
 */
export const logTokenAction = async (userID, actionType, details = {}) => {
  try {
    // Sanitize details to remove null/undefined cardPrintNumber and cardSupply
    const sanitizedDetails = sanitizeActivityDetails(details);
    
    const logRef = collection(db, `users/${userID}/activityLog`);
    const logEntry = {
      actionType,
      timestamp: Timestamp.now(),
      details: sanitizedDetails,
    };
    await addDoc(logRef, logEntry);
  } catch (error) {
    // Error logging token action - fail silently
  }
};
